import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
} from "@angular/core"
import { Router } from "@angular/router"
import { GENERATE_SONG_PLAN_LIMITS } from "@common-lib/constants/constants"
import {
    MAX_LYRICS_LENGTH,
    MAX_PROMPT_LENGTH,
    VocalGender,
} from "@common-lib/interfaces/api/content.api"
import { SubscriptionPlan } from "@common-lib/types/general"
import { Misc } from "@common-lib/modules/misc"
import { BillingService } from "@services/billing.service"
import { ModalService } from "@services/modal.service"
import { TracksService } from "@services/tracks.service"
import { LoginHandoffService } from "@services/login-handoff.service"
import { MenuOptions } from "../reusable/menu-options/menu-options.component"
import { Subscription } from "rxjs"

export type CreateOutputMode = "audio" | "score"

@Component({
    selector: "create",
    templateUrl: "create.component.html",
    styleUrls: ["create.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateComponent implements OnDestroy {
    public prompt: string = ""
    public lyrics: string = ""
    public lyricsEnabled: boolean = true
    public vocalGender: VocalGender | "" = ""
    public lyricsChatOpen: boolean = false
    public lyricsSettingsMenuOpen: boolean = false
    public lyricsSettingsMenuOptions: MenuOptions<null> | undefined
    public lyricsChatWidth: number = 400
    public lyricsChatResizing: boolean = false
    public createLoading: boolean = false
    public generationError: string = ""
    public selectedFile: File | undefined = undefined
    public numCompositions: 1 | 2
    public maxParallelTracks: 1 | 2
    public outputMode: CreateOutputMode = "audio"
    public readonly promptPlaceholder: string =
        "Describe the style, instruments, emotion, pacing, and mood"
    public readonly promptMaxLength: number = MAX_PROMPT_LENGTH
    public readonly lyricsMaxLength: number = MAX_LYRICS_LENGTH
    public readonly lyricsChatMinWidth: number = 320
    public readonly lyricsChatMaxWidth: number = 700
    private readonly subscriptionChanges: Subscription

    constructor(
        private tracks: TracksService,
        private router: Router,
        private ref: ChangeDetectorRef,
        private modalService: ModalService,
        private loginHandoffService: LoginHandoffService,
        private billingService: BillingService
    ) {
        this.maxParallelTracks = this.getPlanParallelTrackLimit()
        this.numCompositions = this.maxParallelTracks
        this.subscriptionChanges = this.billingService.subscription.subscribe(
            () => this.updateParallelTrackLimit()
        )
    }

    public ngOnDestroy(): void {
        this.subscriptionChanges.unsubscribe()
    }

    public async generateTrack(_event: MouseEvent): Promise<void> {
        if (this.createLoading || this.prompt.trim().length === 0) {
            return
        }

        this.createLoading = true
        this.generationError = ""
        this.ref.detectChanges()

        const generation = this.tracks.startSongGeneration({
            prompt: this.prompt,
            instrumental: !this.lyricsEnabled,
            lyrics: this.hasLyrics ? this.lyrics : null,
            vocalGender:
                this.lyricsEnabled && this.vocalGender
                    ? this.vocalGender
                    : null,
            numCompositions: this.numCompositions,
        })
        const acknowledgement = await generation.acknowledgement

        if (!acknowledgement.accepted) {
            this.createLoading = false
            this.generationError = acknowledgement.error ?? ""
            this.ref.detectChanges()
            return
        }

        await this.router.navigate(["/"])
        this.createLoading = false

        await Misc.wait(0.2)

        this.tracks.scrollToIndex$.next(this.tracks.getNumberOfElements())
    }

    public setNumCompositions(count: 1 | 2): void {
        if (!this.createLoading && count <= this.maxParallelTracks) {
            this.numCompositions = count
        }
    }

    private getPlanParallelTrackLimit(): 1 | 2 {
        const plan = this.billingService.getSubscriptionPlan()
        const limits = GENERATE_SONG_PLAN_LIMITS[plan as SubscriptionPlan]

        return limits?.maxParallelTracks ?? 1
    }

    private updateParallelTrackLimit(): void {
        const nextLimit = this.getPlanParallelTrackLimit()

        if (nextLimit === this.maxParallelTracks) {
            return
        }

        this.maxParallelTracks = nextLimit
        if (this.numCompositions > nextLimit) {
            this.numCompositions = nextLimit
        }
        this.ref.markForCheck()
    }

    public setOutputMode(mode: CreateOutputMode): void {
        if (mode === "audio") {
            this.outputMode = "audio"
            return
        }

        this.openScoreGenerationRedirectModal()
    }

    public get hasLyrics(): boolean {
        return this.lyricsEnabled && this.lyrics.trim().length > 0
    }

    public get lyricsPlaceholder(): string {
        return "Write, paste, or generate lyrics for your song"
    }

    public setVocalsIncluded(included: boolean): void {
        this.lyricsEnabled = included

        if (!included) {
            this.lyricsChatOpen = false
            this.closeLyricsSettingsMenu()
        }

        this.ref.detectChanges()
    }

    public setVocalGender(gender: VocalGender | ""): void {
        this.vocalGender = gender
    }

    public openLyricsChat(): void {
        this.lyricsChatOpen = true
    }

    public toggleLyricsSettingsMenu(): void {
        if (this.lyricsSettingsMenuOpen) {
            this.closeLyricsSettingsMenu()
            return
        }

        const selectedGenderIcon = (gender: VocalGender | "") =>
            this.vocalGender === gender
                ? "assets/img/menu/checkmark.svg"
                : undefined

        this.lyricsSettingsMenuOptions = {
            coordinates: { x: 0, y: 0 },
            options: [
                {
                    data: null,
                    icon: "assets/img/navbar/generative_sparkles.svg",
                    text: "Generate lyrics",
                    loading: false,
                    enabled: this.lyricsEnabled,
                    onClick: () => this.openLyricsChat(),
                },
                {
                    data: null,
                    icon: selectedGenderIcon(""),
                    text: "Auto",
                    loading: false,
                    enabled: this.lyricsEnabled,
                    onClick: () => this.setVocalGender(""),
                },
                {
                    data: null,
                    icon: selectedGenderIcon("female"),
                    text: "Female",
                    loading: false,
                    enabled: this.lyricsEnabled,
                    onClick: () => this.setVocalGender("female"),
                },
                {
                    data: null,
                    icon: selectedGenderIcon("male"),
                    text: "Male",
                    loading: false,
                    enabled: this.lyricsEnabled,
                    onClick: () => this.setVocalGender("male"),
                },
            ],
            onClose: () => this.closeLyricsSettingsMenu(),
        }
        this.lyricsSettingsMenuOpen = true
    }

    public closeLyricsSettingsMenu(): void {
        this.lyricsSettingsMenuOpen = false
        this.lyricsSettingsMenuOptions = undefined
    }

    public closeLyricsChat(): void {
        this.lyricsChatOpen = false
        this.lyricsChatResizing = false
    }

    public onLyricsChatResizePointerDown(event: PointerEvent): void {
        const handle = event.currentTarget as HTMLElement

        event.preventDefault()
        this.lyricsChatResizing = true
        this.lyricsChatResizeStartX = event.clientX
        this.lyricsChatResizeStartWidth = this.lyricsChatWidth
        handle.setPointerCapture?.(event.pointerId)
    }

    public onLyricsChatResizePointerMove(event: PointerEvent): void {
        if (!this.lyricsChatResizing) {
            return
        }

        const handle = event.currentTarget as HTMLElement
        const delta = this.lyricsChatResizeStartX - event.clientX
        this.lyricsChatWidth = this.clampLyricsChatWidth(
            this.lyricsChatResizeStartWidth + delta,
            handle
        )
    }

    public onLyricsChatResizePointerUp(event: PointerEvent): void {
        if (!this.lyricsChatResizing) {
            return
        }

        this.onLyricsChatResizePointerMove(event)
        this.lyricsChatResizing = false

        const handle = event.currentTarget as HTMLElement
        handle.releasePointerCapture?.(event.pointerId)
    }

    public onLyricsChatResizePointerCancel(): void {
        this.lyricsChatResizing = false
    }

    public onLyricsChatResizeKeydown(event: KeyboardEvent): void {
        const handle = event.currentTarget as HTMLElement
        let nextWidth = this.lyricsChatWidth

        switch (event.key) {
            case "ArrowLeft":
                nextWidth += 20
                break
            case "ArrowRight":
                nextWidth -= 20
                break
            case "Home":
                nextWidth = this.lyricsChatMinWidth
                break
            case "End":
                nextWidth = this.lyricsChatMaxWidth
                break
            default:
                return
        }

        event.preventDefault()
        this.lyricsChatWidth = this.clampLyricsChatWidth(nextWidth, handle)
    }

    public applyGeneratedLyrics(lyrics: string): void {
        this.lyrics = lyrics
    }

    private lyricsChatResizeStartX: number = 0
    private lyricsChatResizeStartWidth: number = 400

    private openScoreGenerationRedirectModal(): void {
        this.modalService.modals.doActionsBefore.next({
            title: "Switch to Score generation?",
            description:
                "You will be redirected to a legacy version of AIVA that's designed for score generation.",
            continueButtonText: "Continue",
            action: async () => {
                try {
                    await this.loginHandoffService.openLegacyInSameTab()
                    return { success: true }
                } catch (error) {
                    return {
                        success: false,
                        error:
                            error instanceof Error
                                ? error.message
                                : "AIVA 1.0 could not be opened. Please try again.",
                    }
                }
            },
        })
    }

    private clampLyricsChatWidth(width: number, handle: HTMLElement): number {
        const shellWidth = handle.parentElement?.getBoundingClientRect().width
        const availableWidth =
            shellWidth == null ? this.lyricsChatMaxWidth : shellWidth - 420 - 16
        const maximumWidth = Math.max(
            this.lyricsChatMinWidth,
            Math.min(this.lyricsChatMaxWidth, availableWidth)
        )

        return Math.round(
            Math.min(maximumWidth, Math.max(this.lyricsChatMinWidth, width))
        )
    }

    public triggerFileInput(): void {
        const fileInput = document.getElementById(
            "fileInput"
        ) as HTMLInputElement
        if (fileInput) {
            fileInput.click() // Programmatically trigger the file input click
        }
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement

        if (input.files && input.files.length > 0) {
            const file = input.files[0]

            if (file.type.includes("audio")) {
                this.selectedFile = file
                console.log("Selected file:", file)
                // You can now process the file (e.g., upload it or read its content)
            } else {
                console.error(
                    "Invalid file type. Please select an MP3 or WAV file."
                )
            }
        }
    }
}
